import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserPlus, ChevronRight, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../lib/api';

interface PartnerUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  officeName: string | null;
  avatarUrl: string | null;
  city: string | null;
}

interface Invitation {
  id: string;
  status: string;
  direction: 'sent' | 'received';
  message: string | null;
  createdAt: string;
  respondedAt: string | null;
  partner: PartnerUser;
}

function getDisplayName(user: PartnerUser): string {
  if (user.firstName && user.lastName) return `${user.firstName} ${user.lastName}`;
  if (user.displayName) return user.displayName;
  return user.email;
}

function getInitials(user: PartnerUser): string {
  if (user.firstName && user.lastName) return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  if (user.displayName) return user.displayName.slice(0, 2).toUpperCase();
  return user.email.slice(0, 2).toUpperCase();
}

export default function PendingInvitationsBanner() {
  const navigate = useNavigate();
  const { t } = useTranslation('zilloteam');
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getPartnerships({ status: 'pending' });
      if (res.success && res.data) {
        const received = res.data.partnerships.filter((p) => p.direction === 'received');
        setInvitations(received);
      }
    } catch (err) {
      console.error('Error loading pending invitations:', err);
    }
  }, []);

  useEffect(() => {
    load();

    // Listen for real-time refresh signal dispatched by the socket hook
    const handler = () => load();
    window.addEventListener('partnership:refresh', handler);
    return () => window.removeEventListener('partnership:refresh', handler);
  }, [load]);

  const handleAccept = async (inv: Invitation) => {
    setActionInProgress(inv.id);
    try {
      const res = await api.respondToPartnership(inv.id, 'accept');
      if (res.success) {
        toast.success(t('pendingInvitations.partnerNow', { name: getDisplayName(inv.partner) }));
        setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      } else {
        toast.error(res.error?.message || t('pendingInvitations.acceptError'));
      }
    } catch {
      toast.error(t('pendingInvitations.acceptError'));
    } finally {
      setActionInProgress(null);
    }
  };

  const handleDecline = async (inv: Invitation) => {
    setActionInProgress(inv.id);
    try {
      const res = await api.respondToPartnership(inv.id, 'decline');
      if (res.success) {
        toast.success(t('pendingInvitations.declineSuccess'));
        setInvitations((prev) => prev.filter((i) => i.id !== inv.id));
      } else {
        toast.error(res.error?.message || t('pendingInvitations.declineError'));
      }
    } catch {
      toast.error(t('pendingInvitations.declineError'));
    } finally {
      setActionInProgress(null);
    }
  };

  if (invitations.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2.5 shadow-md">
      <div className="flex items-center gap-3">
        {/* Icon + count */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <UserPlus size={18} />
          <span className="text-sm font-semibold">
            {t(invitations.length > 1 ? 'pendingInvitations.count_plural' : 'pendingInvitations.count', { count: invitations.length })}
          </span>
        </div>

        {/* Invitations list (scrollable) */}
        <div className="flex-1 flex items-center gap-2 overflow-x-auto min-w-0 scrollbar-hide">
          {invitations.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-lg px-3 py-1.5 flex-shrink-0"
            >
              {/* Avatar */}
              {inv.partner.avatarUrl ? (
                <img
                  src={inv.partner.avatarUrl.startsWith('http') ? inv.partner.avatarUrl : `http://localhost:3000${inv.partner.avatarUrl}`}
                  alt=""
                  className="w-6 h-6 rounded-full object-cover"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-white/25 text-white text-xs font-semibold flex items-center justify-center">
                  {getInitials(inv.partner)}
                </div>
              )}

              {/* Name + office */}
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-medium truncate max-w-[140px]">
                  {getDisplayName(inv.partner)}
                </span>
                {inv.partner.officeName && (
                  <span className="text-xs text-white/70 truncate max-w-[100px] hidden sm:inline flex items-center gap-0.5">
                    <Building2 size={10} className="inline flex-shrink-0" /> {inv.partner.officeName}
                  </span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleAccept(inv)}
                  disabled={actionInProgress === inv.id}
                  className="px-2.5 py-1 text-xs font-bold bg-blue-500 hover:bg-blue-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  {actionInProgress === inv.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    t('pendingInvitations.accept')
                  )}
                </button>
                <button
                  onClick={() => handleDecline(inv)}
                  disabled={actionInProgress === inv.id}
                  className="px-2.5 py-1 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50"
                >
                  {t('pendingInvitations.decline')}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* See all link */}
        <div className="flex-shrink-0">
          <button
            onClick={() => navigate('/zilloteam/partenaires')}
            className="flex items-center gap-1 text-xs font-medium text-white/80 hover:text-white transition-colors"
          >
            {t('pendingInvitations.viewAll')} <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
